import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { StateAwareLink } from "@components/routes/StateAwareLink";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Menu } from "@design-system/Menu/Menu.lib";
import { StatusPill } from "@design-system/StatusPill/StatusPill";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { ArrowCornerIcon, CloseIcon } from "@icons";
import { TaskInProgressIcon } from "@icons/taskStatuses";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import {
    type WorkRowConfirmationRequest,
    WorkRowEndItems,
} from "../WorkRowActions";
import { getWorkCardContextText } from "../board/WorkBoardCard";
import {
    getWorkOpenClientLink,
    getWorkViewInActionsLink,
} from "../workItemLinks";
import { getWorkFinishedStatus } from "../board/workBoardStatus";
import styles from "./WorkDetailPane.module.scss";

interface WorkDetailPaneHeaderProps {
    row: ClientsWorkOverviewWorkRow;
    onClose: () => void;
    onConfirmationRequest: (request: WorkRowConfirmationRequest) => void;
}

/** Done or Canceled for finished work, else the current stage; none without a stage. */
const WorkStagePill = ({ row }: { row: ClientsWorkOverviewWorkRow }) => {
    const status = getWorkFinishedStatus(row);
    if (status) {
        return (
            <StatusPill
                icon={<status.Icon className="icon-color-revert" aria-hidden />}
                label={status.label}
            />
        );
    }
    return row.closeStageName ? (
        <StatusPill
            icon={
                <TaskInProgressIcon className="icon-color-revert" aria-hidden />
            }
            label={row.closeStageName}
        />
    ) : null;
};

/** The pane's header: who and what the work is, its stage, and its actions. */
export const WorkDetailPaneHeader = ({
    row,
    onClose,
    onConfirmationRequest,
}: Readonly<WorkDetailPaneHeaderProps>) => {
    const viewInActionsLink = getWorkViewInActionsLink(row);
    const openClientLink = getWorkOpenClientLink(row);

    return (
        <header className={styles.header}>
            <HStack gap={8} align="center" justify="between">
                <HStack gap={8} align="center" className={styles.identity}>
                    <EntityAvatar
                        size="sm"
                        entity={{
                            name: row.entityName,
                            iconColor: row.entityIconColor,
                            logo: row.entityLogo,
                        }}
                        aria-hidden
                    />
                    <h2 className={styles.title}>{row.entityName}</h2>
                </HStack>
                <HStack gap={4} align="center">
                    {viewInActionsLink && (
                        <Button
                            variant="secondary"
                            size="sm"
                            asChild
                            rightIcon={<ArrowCornerIcon />}
                        >
                            <StateAwareLink to={viewInActionsLink}>
                                View in Actions
                            </StateAwareLink>
                        </Button>
                    )}
                    <Menu.More aria-label="Work item actions">
                        {openClientLink && (
                            <Menu.LinkItem to={openClientLink}>
                                Open client
                            </Menu.LinkItem>
                        )}
                        <WorkRowEndItems
                            row={row}
                            onConfirmationRequest={onConfirmationRequest}
                        />
                    </Menu.More>
                    <Tooltip shortcut="escape" side="bottom">
                        <Button
                            variant="secondary"
                            size="sm"
                            iconOnly
                            aria-label="Close details"
                            onClick={onClose}
                        >
                            <CloseIcon />
                        </Button>
                    </Tooltip>
                </HStack>
            </HStack>
            <p className={styles.context}>
                {getWorkCardContextText({ row, isClientShown: true })}
            </p>
            <div>
                <WorkStagePill row={row} />
            </div>
        </header>
    );
};
